/** Dispositivos: tablet kiosko y agente de acceso — contracts.md §3. */
import { z } from "zod";
import { ACCESS_METHODS, ACCESS_RESULTS, DEVICE_KINDS, DEVICE_PAIRING_STATUSES, DEVICE_STATUSES, } from "../enums.js";
import { objectId, reasonRequired } from "./base.js";
export const createDeviceBody = z.object({
    kind: z.enum(DEVICE_KINDS),
    branchId: objectId,
    locationId: objectId.optional(),
    name: z.string().trim().min(2).max(80),
});
/**
 * Para qué se usa una tablet. Las vinculadas antes de que existiera el campo quedan con
 * semáforo y kiosko (lo que ya hacían) y sin abrir molinete.
 */
export const deviceCapabilities = z.object({
    semaforo: z.boolean(),
    kiosko: z.boolean(),
    abrirMolinete: z.boolean(),
});
export const LEGACY_DEVICE_CAPABILITIES = {
    semaforo: true,
    kiosko: true,
    abrirMolinete: false,
};
export const deviceDto = z.object({
    id: objectId,
    kind: z.enum(DEVICE_KINDS),
    branchId: objectId,
    locationId: objectId.nullable(),
    name: z.string(),
    status: z.enum(DEVICE_STATUSES),
    lastHeartbeatAt: z.coerce.date().nullable(),
    /** `null` = nunca reportó: no es «offline», es «no se pudo ver» (disciplina del null). */
    online: z.boolean().nullable(),
    capabilities: deviceCapabilities,
});
/** La api key viaja UNA sola vez, al alta. Después sólo vive su hash. */
export const createDeviceResponse = z.object({
    device: deviceDto,
    apiKey: z.string(),
});
export const heartbeatBody = z.object({
    /** Estado que reporta el dispositivo (versión del agente, cola offline pendiente…). */
    state: z.record(z.string(), z.unknown()).optional(),
});
export const heartbeatResponse = z.object({
    ok: z.literal(true),
    state: z.object({
        accessPointState: z.string().nullable(),
        snapshotStale: z.boolean().nullable(),
        serverTime: z.coerce.date(),
    }),
});
export const revokeDeviceBody = z.object({ reason: reasonRequired });
// ── Usos de la tablet y vinculación por código ──────────────────────────────
/** `GET /api/devices/me`: quién es el dispositivo que llama. */
export const kioskIdentity = z.object({
    id: objectId,
    kind: z.enum(DEVICE_KINDS),
    name: z.string(),
    branchId: objectId,
    locationId: objectId.nullable(),
    capabilities: deviceCapabilities,
});
/** Código de vinculación: 6 caracteres legibles, sin 0/O/1/I para que no se confundan. */
export const PAIRING_CODE_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
export const PAIRING_CODE_LENGTH = 6;
export const PAIRING_TTL_MINUTES = 10;
export const PAIRING_TOKEN_HEADER = "x-pairing-token";
/** Lo que tipea la persona: se aceptan minúsculas, espacios y guiones. */
export const pairingCode = z
    .string()
    .transform((value) => value.replace(/[\s-]/g, "").toUpperCase())
    .pipe(z
    .string()
    .regex(new RegExp(`^[${PAIRING_CODE_ALPHABET}]{${PAIRING_CODE_LENGTH}}$`), "El código tiene 6 letras y números, como lo muestra la tablet."));
/** `POST /api/devices/pairings` (público): la tablet pide un código. */
export const createPairingResponse = z.object({
    pairingId: objectId,
    code: z.string(),
    /** Secreto de esa tablet para consultar el estado. En la base sólo vive su hash. */
    pollToken: z.string(),
    expiresAt: z.coerce.date(),
});
/** `GET /api/devices/pairings/:id`: la api key viaja UNA vez, al reclamar. */
export const pairingStatusResponse = z.object({
    status: z.enum(DEVICE_PAIRING_STATUSES),
    expiresAt: z.coerce.date(),
    apiKey: z.string().optional(),
    device: kioskIdentity.optional(),
});
/** `POST /api/devices/pairings/approve`: alguien con sesión aprueba el código. */
export const approvePairingBody = z.object({
    code: pairingCode,
    branchId: objectId,
    locationId: objectId.nullable().optional(),
    name: z.string().trim().min(2).max(80),
    capabilities: deviceCapabilities,
});
// ── Punto de acceso ─────────────────────────────────────────────────────────
export const accessValidateBody = z.object({
    accessPointId: objectId,
    method: z.enum(["fingerprint", "member_qr"]),
    identifier: z.string().min(1).max(512),
});
export const accessValidateResponse = z.object({
    result: z.enum(ACCESS_RESULTS),
    reason: z.string().nullable(),
    person: z
        .object({
        kind: z.string(),
        id: objectId,
        name: z.string(),
        memberNumber: z.number().int().nullable(),
        badges: z.array(z.string()),
    })
        .nullable(),
    accessLogId: objectId,
});
/** Snapshot de habilitados para contingencia (Arquitectura §8). */
export const accessSnapshotResponse = z.object({
    generatedAt: z.coerce.date(),
    entries: z.array(z.object({
        personId: objectId,
        light: z.enum(["green", "yellow", "red"]),
        reason: z.string().nullable(),
        fingerprints: z.array(z.string()),
    })),
});
/** Cola offline del agente (RN-ACC-25). */
export const contingencySyncBody = z.object({
    entries: z
        .array(z.object({
        accessPointId: objectId,
        personId: objectId.optional(),
        method: z.enum(ACCESS_METHODS),
        identifier: z.string().optional(),
        ts: z.coerce.date(),
    }))
        .max(1000),
});
//# sourceMappingURL=devices.js.map